"use client";

import { useCallback, useEffect, useMemo } from "react";
import { useColumn } from "@/hooks/use-column";
import ControlledTable from "@/components/controlled-table";
import { getColumns } from "./columns";
import useApi from "@/hooks/useApi";
import { IResponseData } from "@/utilities/types/requests";
import { ISiteContent } from "@/utilities/types/SiteContent";
import SiteContentRepository from "@/utilities/repositories/SiteContent";
import { Title } from "rizzui";
import useProfile from "@/hooks/use-profile";
import AddNewButton from "@/app/shared/add-new-button";
import { routes } from "@/config/routes";
import { Roles } from "@/utilities/types/Users";

export default function SiteContentTable() {
  const { profile } = useProfile();
  const { request: deleteSiteContent, loading: isDeleting } = useApi<
    IResponseData<ISiteContent>
  >({
    request: SiteContentRepository.deleteSiteContent,
    enableToast: true,
  });
  const {
    request: fetchData,
    response,
    loading: isLoading,
  } = useApi({
    request:
      profile?.role === Roles.ADMIN
        ? SiteContentRepository.adminGetAllSiteContent
        : SiteContentRepository.getAllSiteContent,
  });

  const tableData = response?.data || [];

  const onDeleteItem = useCallback(
    async (id: string) => {
      await deleteSiteContent(id);
      await fetchData();
    },
    [deleteSiteContent, fetchData]
  );

  const columns = useMemo(
    () =>
      getColumns({
        role: profile?.role,
        onDeleteItem,
        isDeleting,
      }),
    [isDeleting, onDeleteItem, profile?.role]
  );

  const { visibleColumns } = useColumn(columns);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="mt-4">
      <div className="relative z-50 mb-4 flex flex-wrap items-center justify-between gap-2.5 @container">
        <Title as="h5" className="@xl:basis-auto text-xl md:text-2xl">
          Pages
        </Title>
        {profile?.role === Roles.AGENCY && (
          <AddNewButton
            label="Create Page"
            navigateTo={routes.siteContent.create}
          />
        )}
        {/* Admin system-page creation is temporarily disabled until required. */}
      </div>
      <ControlledTable
        variant="modern"
        data={tableData}
        isLoading={isLoading}
        showLoadingText={true}
        columns={visibleColumns}
        className="rounded-md border border-muted text-sm shadow-sm [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:h-60 [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:justify-center [&_.rc-table-row:last-child_td.rc-table-cell]:border-b-0 [&_thead.rc-table-thead]:border-t-0"
      />
    </div>
  );
}
